import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router'
import { useAppData, useBillContext } from '../../data'
import {
  bcnLawUrl,
  formatDateShort,
  voteResultLabel,
  voteResultSymbol,
  voteResultTone,
} from '../../helpers'
import { extractVoteSlug } from '../../discovery'
import { VoteDetailPanel } from '../vote_detail/VoteDetailPanel'
import { buildPhases } from './phases'
import {
  groupVotesByDate,
  groupVotesByType,
  voteTypeExplanation,
} from './grouping'
import { scrollToElement, scrollIfOffscreen, setVoteHash } from './voteLinks'
import type { VoteEntry } from './grouping'
import type { VoteSession } from '../../models'

// ---------------------------------------------------------------------------
// VotePill
// ---------------------------------------------------------------------------

interface VotePillProps {
  voteSlug: string
  vote: VoteSession
  typeLabel: string
  showArrow: boolean
  isActive: boolean
  boletin: string
  onActivate: (slug: string) => void
  onDeactivate: () => void
}

function VotePill({
  voteSlug,
  vote,
  typeLabel,
  showArrow,
  isActive,
  boletin,
  onActivate,
  onDeactivate,
}: VotePillProps) {
  const tone = voteResultTone(vote)
  const symbol = voteResultSymbol(vote)
  const label = voteResultLabel(vote)
  const counts = `${vote.votes_for}-${vote.votes_against}`
  const tip = voteTypeExplanation(typeLabel)

  function handleClick() {
    if (isActive) {
      onDeactivate()
    } else {
      onActivate(voteSlug)
      setVoteHash(voteSlug, boletin)
      const scrollId = `vote-detail-${voteSlug}`
      setTimeout(() => scrollIfOffscreen(scrollId), 100)
    }
  }

  return (
    <>
      {showArrow && <span className="vote-arrow">→</span>}
      <span
        className={`vote-pill ${tone}${isActive ? ' active' : ''}`}
        onClick={handleClick}
      >
        <span className="type-label" title={tip}>
          {typeLabel}
        </span>
        {` ${symbol} ${label} `}
        <span className="count">{counts}</span>
      </span>
    </>
  )
}

// ---------------------------------------------------------------------------
// VotePills (flat list)
// ---------------------------------------------------------------------------

interface VotePillsProps {
  votes: VoteEntry[]
  activeVote: string | null
  boletin: string
  onActivate: (slug: string) => void
  onDeactivate: () => void
}

function VotePills({
  votes,
  activeVote,
  boletin,
  onActivate,
  onDeactivate,
}: VotePillsProps) {
  let prevType = ''
  return (
    <>
      {votes.map(([slug, vote, typeLabel]) => {
        const showArrow = prevType !== '' && prevType !== typeLabel
        prevType = typeLabel
        return (
          <VotePill
            key={slug}
            voteSlug={slug}
            vote={vote}
            typeLabel={typeLabel}
            showArrow={showArrow}
            isActive={activeVote === slug}
            boletin={boletin}
            onActivate={onActivate}
            onDeactivate={onDeactivate}
          />
        )
      })}
    </>
  )
}

// ---------------------------------------------------------------------------
// ActiveDetail (the expanded VoteDetailPanel below the pills)
// ---------------------------------------------------------------------------

interface ActiveDetailProps {
  votes: VoteEntry[]
  activeVote: string | null
  closing: boolean
}

function ActiveDetail({ votes, activeVote, closing }: ActiveDetailProps) {
  if (activeVote == null) return null
  const found = votes.find(([slug]) => slug === activeVote)
  if (found == null) return null
  const [slug, vote] = found
  const detailId = `vote-detail-${slug}`
  return (
    <div id={detailId} className={closing ? 'closing' : undefined}>
      <VoteDetailPanel vote={vote} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// VoteDateGroups (grouped by date, then by type — for complex bills)
// ---------------------------------------------------------------------------

interface VoteDateGroupsProps {
  votes: VoteEntry[]
  activeVote: string | null
  boletin: string
  closing: boolean
  onActivate: (slug: string) => void
  onDeactivate: () => void
}

function VoteDateGroups({
  votes,
  activeVote,
  boletin,
  closing,
  onActivate,
  onDeactivate,
}: VoteDateGroupsProps) {
  const [expandedDate, setExpandedDate] = useState<string | null>(null)
  const grouped = groupVotesByDate(votes)

  return (
    <div className="vote-date-groups">
      {grouped.map(([date, dateVotes]) => {
        const dateDisplay = formatDateShort(date)
        const typeGroups = groupVotesByType(dateVotes)
        const summary = typeGroups
          .map(([vtype, items]) => `${items.length} ${vtype.toLowerCase()}`)
          .join(' · ')

        const approved = dateVotes.filter(([, v]) => voteResultTone(v) === 'approved').length
        const amendment = dateVotes.filter(([, v]) => voteResultTone(v) === 'amendment').length
        const rejected = dateVotes.filter(([, v]) => voteResultTone(v) === 'rejected').length
        const total = dateVotes.length

        const approvedPct = total > 0 ? (approved / total) * 100 : 100
        const amendmentPct = total > 0 ? (amendment / total) * 100 : 0
        const rejectedPct = total > 0 ? (rejected / total) * 100 : 0

        const isOpen = expandedDate === date

        function toggleDate() {
          setExpandedDate(isOpen ? null : date)
        }

        return (
          <div key={date} className="vote-date-group">
            <div className="vote-date-header" onClick={toggleDate}>
              <span className="vote-date-label">{dateDisplay}</span>
              <div className="vote-date-bar">
                <div className="bar-for" style={{ width: `${approvedPct}%` }} />
                <div className="bar-amendment" style={{ width: `${amendmentPct}%` }} />
                <div className="bar-against" style={{ width: `${rejectedPct}%` }} />
              </div>
              <span className="vote-date-summary">{summary}</span>
              <span className="vote-date-chevron">{isOpen ? '▾' : '›'}</span>
            </div>
            {isOpen && (
              <div className="vote-type-sections">
                {typeGroups.map(([vtype, typeVotes]) => {
                  const tApproved = typeVotes.filter(([, v]) => voteResultTone(v) === 'approved').length
                  const tAmendment = typeVotes.filter(([, v]) => voteResultTone(v) === 'amendment').length
                  const tRejected = typeVotes.filter(([, v]) => voteResultTone(v) === 'rejected').length
                  const summaryParts: string[] = []
                  if (tApproved > 0) summaryParts.push(`${tApproved} ✓`)
                  if (tAmendment > 0) summaryParts.push(`${tAmendment} ↺`)
                  if (tRejected > 0) summaryParts.push(`${tRejected} ✗`)
                  const typeSummary =
                    summaryParts.length === 0
                      ? `${typeVotes.length}`
                      : `${typeVotes.length} (${summaryParts.join(', ')})`
                  const tip = voteTypeExplanation(vtype)
                  return (
                    <div key={vtype} className="vote-type-section">
                      <div className="vote-type-label">
                        <span className="vote-type-name">{vtype}</span>
                        <span className="info-tip" tabIndex={0}>
                          ?
                          <span className="info-tip-text">{tip}</span>
                        </span>
                        <span className="vote-type-count">{typeSummary}</span>
                      </div>
                      <div className="vote-row vote-row--nested">
                        <VotePills
                          votes={typeVotes}
                          activeVote={activeVote}
                          boletin={boletin}
                          onActivate={onActivate}
                          onDeactivate={onDeactivate}
                        />
                      </div>
                      <ActiveDetail
                        votes={typeVotes}
                        activeVote={activeVote}
                        closing={closing}
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// LifecycleSection
// ---------------------------------------------------------------------------

export function LifecycleSection() {
  const appData = useAppData()
  const billCtx = useBillContext()
  const location = useLocation()

  const bill =
    billCtx.selectedBoletin != null
      ? appData.cachedBill(billCtx.selectedBoletin)
      : undefined

  const [activeVote, setActiveVoteRaw] = useState<string | null>(null)
  const [closing, setClosing] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Build phases once; reused in both render and the effect below
  const phases = useMemo(() => bill != null ? buildPhases(bill) : [], [bill])

  // Activate from hash deep-link when bill becomes ready.
  // Compute the desired slug in a single pass to avoid stale-closure reads.
  useEffect(() => {
    if (bill == null) return

    const slug = extractVoteSlug(location.hash)

    const execPhase =
      phases.length > 0 &&
      phases[phases.length - 1]!.anchorId === 'lifecycle-step-ejecutivo'

    // Determine the slug to activate (null = deactivate)
    const slugValid = slug != null && phases.some((p) => p.votes.some(([s]) => s === slug))
    const desiredSlug: string | null =
      slugValid && !(execPhase && slug == null) ? slug : null

    setActiveVoteRaw((prev) => {
      const prevValid =
        prev != null && phases.some((p) => p.votes.some(([s]) => s === prev))

      // If prev is already valid and no conflicting condition, keep it unless
      // we have a new desired slug or exec-phase reset applies
      if (desiredSlug == null) {
        // Invalid slug or no hash: reset unless prev is still valid and no exec reset
        if (!prevValid || (execPhase && slug == null)) return null
        return prev
      }

      // Valid slug from hash: activate it (schedule scroll only on first activation)
      if (prev !== desiredSlug) {
        const scrollId = `vote-detail-${desiredSlug}`
        if (scrollTimerRef.current != null) clearTimeout(scrollTimerRef.current)
        scrollTimerRef.current = setTimeout(() => scrollToElement(scrollId), 150)
      }
      return desiredSlug
    })
  }, [phases, location.hash])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) clearTimeout(closeTimerRef.current)
      if (scrollTimerRef.current != null) clearTimeout(scrollTimerRef.current)
    }
  }, [])

  const handleActivate = useCallback((slug: string) => {
    if (closeTimerRef.current != null) clearTimeout(closeTimerRef.current)
    setClosing(false)
    setActiveVoteRaw(slug)
  }, [])

  const handleDeactivate = useCallback(() => {
    setClosing(true)
    if (closeTimerRef.current != null) clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(() => {
      setActiveVoteRaw(null)
      setClosing(false)
    }, 250)
  }, [])

  // Derive heading
  const isPublished = bill?.status.includes('Publicado') ?? false
  const lawText =
    bill?.law_number != null && bill.law_number !== '' ? bill.law_number : null
  const lawUrl = lawText != null ? bcnLawUrl(lawText) : null

  return (
    <div className="section-container">
      {isPublished ? (
        <h2 className="section-title">
          Proyecto de Ley publicado como{' '}
          {lawUrl != null ? (
            <a
              href={lawUrl}
              target="_blank"
              rel="noopener"
              className="accent accent-link"
            >
              {lawText ?? 'Ley'}
            </a>
          ) : (
            <span className="accent">{lawText ?? 'Ley'}</span>
          )}
        </h2>
      ) : (
        <h2 className="section-title">
          ¿En qué va <span className="accent">este proyecto</span>?
        </h2>
      )}

      {bill != null && (
        <div className="stepper">
          {phases.map((phase) => {
            const {
              anchorId,
              icon,
              votes,
              chamber,
              role,
              dateRange,
              changeSummary,
              lawUrl: phaseLawUrl,
            } = phase
            const hasDate = dateRange !== ''
            const hasChange = changeSummary !== ''
            const hasVotes = votes.length > 0
            const boletin = bill.boletin

            const iconChar =
              icon === 'approved' ? '✓' : icon === 'rejected' ? '✗' : '⏳'
            const lineClass = icon === 'pending' ? 'pending' : 'done'

            return (
              <div key={anchorId} id={anchorId} className="step">
                <div className="step-track">
                  <div className={`step-dot ${icon}`}>{iconChar}</div>
                  <div className={`step-line ${lineClass}`} />
                </div>
                <div className="step-content">
                  <div className="step-header">
                    <span className="step-chamber">{chamber}</span>
                    <span className="step-role">{role}</span>
                  </div>
                  {hasDate && (
                    <div className="step-date">{dateRange}</div>
                  )}
                  {hasChange && (
                    <div className="step-change">{changeSummary}</div>
                  )}
                  {phaseLawUrl != null && (
                    <a
                      href={phaseLawUrl}
                      target="_blank"
                      rel="noopener"
                      className="step-law-link"
                    >
                      Leer texto de la ley ↗
                    </a>
                  )}
                  {hasVotes && (
                    <>
                      {votes.length <= 8 ? (
                        <>
                          <div className="vote-row">
                            <VotePills
                              votes={votes}
                              activeVote={activeVote}
                              boletin={boletin}
                              onActivate={handleActivate}
                              onDeactivate={handleDeactivate}
                            />
                          </div>
                          <ActiveDetail
                            votes={votes}
                            activeVote={activeVote}
                            closing={closing}
                          />
                        </>
                      ) : (
                        <VoteDateGroups
                          votes={votes}
                          activeVote={activeVote}
                          boletin={boletin}
                          closing={closing}
                          onActivate={handleActivate}
                          onDeactivate={handleDeactivate}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
